/**
 * <mb-article-toc> — marks the jump link for the section being read with
 * aria-current="location". Links and heading ids are server-rendered, so the
 * list still works as plain anchors without this script.
 */

if (!customElements.get('mb-article-toc')) {
  class MBArticleToc extends HTMLElement {
    #cleanups = [];
    #links = [];
    #targets = [];
    #frame = 0;

    connectedCallback() {
      this.#links = [...this.querySelectorAll('a[href^="#"]')];
      this.#targets = this.#links
        .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
        .filter(Boolean);
      if (!this.#targets.length) return;

      const onScroll = () => {
        if (this.#frame) return;
        this.#frame = requestAnimationFrame(() => {
          this.#frame = 0;
          this.#update();
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      this.#cleanups.push(() => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        cancelAnimationFrame(this.#frame);
      });
      this.#update();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((cleanup) => cleanup());
      this.#cleanups = [];
    }

    // A section counts as current once its heading passes a reading line just
    // below where a jump link parks it (its scroll-margin-top).
    #update() {
      const readingLine = (parseFloat(getComputedStyle(this.#targets[0]).scrollMarginTop) || 0) + 80;
      let currentId = null;
      for (const target of this.#targets) {
        if (target.getBoundingClientRect().top > readingLine) break;
        currentId = target.id;
      }
      this.#links.forEach((link) => {
        if (decodeURIComponent(link.hash.slice(1)) === currentId) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('mb-article-toc', MBArticleToc);
}
